import { ArrowLeft, Search } from "lucide-react";
import { useEffect, useState } from "react";
import {
	isRouteErrorResponse,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLocation,
	useNavigate,
} from "react-router";

import type { Route } from "./+types/root";
import "./app.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CookieConsentBanner } from "~/components/cookie-consent-banner";
import { NavigationProgress } from "~/components/navigation-progress";
import { Toaster } from "~/components/ui/sonner";
import { trackEvent } from "~/lib/gtag";

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

export const links: Route.LinksFunction = () => [
	{ rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
	{ rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
	{ rel: "preconnect", href: "https://fonts.googleapis.com" },
	{
		rel: "preconnect",
		href: "https://fonts.gstatic.com",
		crossOrigin: "anonymous",
	},
	{
		rel: "stylesheet",
		href: "https://fonts.googleapis.com/css2?family=Manrope:wght@400..800&family=JetBrains+Mono:wght@400..700&display=swap",
	},
	...(GA4_ID
		? [
				{ rel: "preconnect", href: "https://www.googletagmanager.com" },
				{ rel: "preconnect", href: "https://www.google-analytics.com" },
			]
		: []),
];

export function Layout({ children }: { children: React.ReactNode }) {
	return (
		<html lang="cs">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
				{GA4_ID && (
					<>
						{/* Consent Mode v2 defaults — must run BEFORE GA4 loads */}
						<script
							// biome-ignore lint/security/noDangerouslySetInnerHtml: required for GA4 Consent Mode v2 initialization
							dangerouslySetInnerHTML={{
								__html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{analytics_storage:'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',wait_for_update:500});gtag('js',new Date());`,
							}}
						/>
						{/* GA4 script — analytics_storage is 'denied' by default */}
						<script async src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`} />
						{/* GA4 config — no page_view sent until consent is granted */}
						<script
							// biome-ignore lint/security/noDangerouslySetInnerHtml: required for GA4 config
							dangerouslySetInnerHTML={{
								__html: `gtag('config','${GA4_ID}',{send_page_view:false});`,
							}}
						/>
					</>
				)}
			</head>
			<body>
				{children}
				<Toaster />
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

function ReactQueryDevtoolsClient() {
	const [DevTools, setDevTools] = useState<React.ComponentType<{ initialIsOpen: boolean }> | null>(
		null,
	);

	useEffect(() => {
		import("@tanstack/react-query-devtools").then((m) => {
			setDevTools(() => m.ReactQueryDevtools);
		});
	}, []);

	if (!DevTools) return null;
	return <DevTools initialIsOpen={false} />;
}

export default function App() {
	const [queryClient] = useState(() => new QueryClient());
	const location = useLocation();

	// Track SPA page views on client-side navigation
	const pathname = location.pathname;
	useEffect(() => {
		trackEvent("page_view", {
			page_location: `${window.location.origin}${pathname}`,
			page_title: document.title,
		});
	}, [pathname]);

	return (
		<QueryClientProvider client={queryClient}>
			<NavigationProgress />
			<Outlet />
			<CookieConsentBanner />
			<ReactQueryDevtoolsClient />
		</QueryClientProvider>
	);
}

export function meta({ error }: Route.MetaArgs) {
	const defaultOgTags = [
		{ property: "og:site_name", content: "FirmIndex" },
		{ property: "og:locale", content: "cs_CZ" },
		{ property: "og:type", content: "website" },
		{ property: "og:image", content: "https://firmindex.cz/og-default.png" },
		{ property: "og:image:width", content: "1200" },
		{ property: "og:image:height", content: "630" },
		{ name: "twitter:card", content: "summary_large_image" },
		{ name: "twitter:image", content: "https://firmindex.cz/og-default.png" },
	];

	if (error && isRouteErrorResponse(error) && error.status === 404) {
		return [
			{ title: "Stránka nenalezena | FirmIndex" },
			{ name: "robots", content: "noindex, nofollow" },
			...defaultOgTags,
		];
	}
	if (error) {
		return [
			{ title: "Chyba | FirmIndex" },
			{ name: "robots", content: "noindex, nofollow" },
			...defaultOgTags,
		];
	}

	// Default meta for pages that don't define their own meta
	return [
		{ title: "FirmIndex – Finanční data českých firem na jednom místě" },
		{
			name: "description",
			content:
				"Vyhledávejte české firmy podle názvu nebo IČO. Obchodní rejstřík, finanční údaje, statutární orgány a další informace na jednom místě.",
		},
		...defaultOgTags,
	];
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	// Report non-404 errors to Sentry (client-side only)
	if (typeof window !== "undefined" && !isRouteErrorResponse(error)) {
		import("~/lib/sentry").then(({ Sentry }) => Sentry.captureException(error));
	}

	const is404 = isRouteErrorResponse(error) && error.status === 404;

	if (is404) {
		return <NotFoundPage />;
	}

	let details = "Nastala neočekávaná chyba.";
	let stack: string | undefined;

	if (isRouteErrorResponse(error)) {
		details = error.statusText || details;
	} else if (import.meta.env.DEV && error && error instanceof Error) {
		details = error.message;
		stack = error.stack;
	}

	return (
		<main className="flex min-h-screen flex-col items-center justify-center p-4">
			<div className="flex flex-col items-center gap-4 text-center">
				<h1 className="text-6xl font-bold text-primary">Chyba</h1>
				<p className="text-lg text-muted-foreground">{details}</p>
				<Link to="/" className="btn btn-primary btn-sm mt-4">
					Zpět na úvod
				</Link>
				{stack && (
					<pre className="mt-8 w-full max-w-2xl overflow-x-auto rounded-lg bg-base-200 p-4 text-left text-xs">
						<code>{stack}</code>
					</pre>
				)}
			</div>
		</main>
	);
}

function NotFoundPage() {
	const navigate = useNavigate();
	const [query, setQuery] = useState("");

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = query.trim();
		if (trimmed) {
			navigate(`/search?q=${encodeURIComponent(trimmed)}`);
		}
	};

	return (
		<main className="flex min-h-screen flex-col bg-background">
			{/* Header with logo */}
			<header className="border-b border-gray-100 bg-surface/80 px-6 py-4">
				<Link to="/">
					<img src="/logo.png" alt="FirmIndex" className="h-10" />
				</Link>
			</header>

			{/* Content */}
			<div className="flex flex-1 flex-col items-center justify-center px-4 py-16">
				<div className="flex w-full max-w-lg flex-col items-center gap-6 text-center">
					<h1 className="text-8xl font-extrabold text-primary">404</h1>
					<div className="flex flex-col gap-2">
						<h2 className="text-2xl font-bold text-text">Stránka nenalezena</h2>
						<p className="text-muted">
							Omlouváme se, ale požadovaná stránka neexistuje nebo byla přesunuta.
						</p>
					</div>

					{/* Search bar */}
					<div className="mt-4 w-full">
						<p className="mb-3 text-sm font-medium text-muted">
							Zkuste vyhledat firmu, kterou hledáte:
						</p>
						<form onSubmit={handleSearch} className="flex gap-2">
							<div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
								<Search className="h-4 w-4 shrink-0 text-muted" />
								<input
									type="text"
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder="Název firmy nebo IČO..."
									className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
								/>
							</div>
							<button type="submit" className="btn btn-primary btn-sm">
								Hledat
							</button>
						</form>
					</div>

					{/* Navigation links */}
					<div className="mt-2 flex gap-3">
						<Link
							to="/"
							className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-text transition-colors hover:bg-gray-50"
						>
							<ArrowLeft className="h-4 w-4" />
							Zpět na úvod
						</Link>
						<Link
							to="/search"
							className="inline-flex items-center gap-2 rounded-lg bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
						>
							<Search className="h-4 w-4" />
							Procházet firmy
						</Link>
					</div>
				</div>
			</div>
		</main>
	);
}
